// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { NavigationControls } from '@client/banks/layouts/navigation-controls';
import { ScreenLayout } from '@client/banks/layouts/screen-layout';
import { Alert, Typography } from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import catalogStyles from '../styles/institution-catalog.module.css';
import { ReviewChecklistRow } from './review-checklist-row';
import styles from './review-summary.module.css';
import { useReviewSummary } from './use-review-summary';

export const ReviewSummary = defineComponent({
  displayName: 'ReviewSummary',
  useSetup: useReviewSummary,
  View: ({
    bankName,
    checklistItems,
    showPendingBanner,
    canSubmit,
    onReviewSection,
    onConfirm,
    isSaving,
  }) => (
    <ScreenLayout
      actions={
        <NavigationControls
          nextLabel={
            <Translation
              i18nKey="aggregator.create-bank.review.submit"
              ns="aggregator"
            />
          }
          onNext={onConfirm}
          nextDisabled={!canSubmit}
          previousStep="accounts-list"
          loading={isSaving}
          previousDisabled={isSaving}
        />
      }
    >
      <div
        className={catalogStyles['step-body']}
        data-testid="bank-upload-review-summary"
      >
        <header className={catalogStyles['step-header']}>
          <Typography
            component="h2"
            variant="h6"
            className={catalogStyles['step-title']}
          >
            <Translation
              i18nKey="aggregator.create-bank.review.title"
              ns="aggregator"
            />
          </Typography>
          <Typography variant="body2" className={catalogStyles['step-description']}>
            <Translation
              i18nKey="aggregator.create-bank.review.description"
              ns="aggregator"
            />
          </Typography>
        </header>

        <ul
          className={styles.checklist}
          aria-label={bankName}
          data-testid="bank-upload-review-checklist"
        >
          {checklistItems.map((item, index, items) => (
            <ReviewChecklistRow
              key={item.id}
              item={item}
              isLastRow={index === items.length - 1}
              onReview={() => onReviewSection(item.listStep)}
            />
          ))}
        </ul>

        {showPendingBanner ? (
          <Alert
            severity="warning"
            icon={<AppIcon icon="Info" color="inherit" aria-hidden />}
            className={styles['pending-banner']}
            data-testid="bank-upload-review-pending-banner"
          >
            <Typography variant="body2" className={styles['pending-banner-text']}>
              <Translation
                i18nKey="aggregator.create-bank.review.pending-banner"
                ns="aggregator"
              />
            </Typography>
          </Alert>
        ) : null}
      </div>
    </ScreenLayout>
  ),
});
